import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { calculateDateRange } from '../utils/dateUtils';

// Types for the different tab and view states
export type TimingTab = 'weekly' | 'monthly' | 'yearly';
export type AssetTab = 'finance' | 'liquidity';
export type AnalysisType = 'distribution' | 'trends';
export type ExpenseCardTab = 'total-expense' | 'total-income' | 'net-flow' | 'money-out' | 'money-in' | 'net-cash-flow';
export type GroupBy = 'categoryGroup' | 'category';

// Interface for the complete overview state
export interface OverviewState {
  // Income and Expenses tab states
  timingTab: TimingTab;
  assetTab: AssetTab;
  analysisType: AnalysisType;
  expenseCardTab: ExpenseCardTab;
  startDate: Date;
  endDate: Date;

  // Group by state
  groupBy: GroupBy;

  // Trending accumulate state
  trendingAccumulate: boolean;
}

// Default values
const getDefaultDateRange = () => {
  const now = new Date();
  return calculateDateRange(now, 'monthly');
};

export const DEFAULT_OVERVIEW_STATE: OverviewState = {
  timingTab: 'monthly',
  assetTab: 'finance',
  analysisType: 'distribution',
  expenseCardTab: 'total-expense',
  ...getDefaultDateRange(),
  groupBy: 'categoryGroup',
  trendingAccumulate: false,
};

// Context interface
interface OverviewContextType {
  state: OverviewState;
  setTimingTab: (tab: TimingTab) => void;
  setAssetTab: (tab: AssetTab) => void;
  setAnalysisType: (type: AnalysisType) => void;
  setExpenseCardTab: (tab: ExpenseCardTab) => void;
  setSelectedDate: (date: Date) => void;
  setStartDate: (date: Date) => void;
  setEndDate: (date: Date) => void;
  setDateRange: (startDate: Date, endDate: Date) => void;
  setGroupBy: (groupBy: GroupBy) => void;
  setTrendingAccumulate: (accumulate: boolean) => void;
  updateState: (updates: Partial<OverviewState>) => void;
}

// Create context
const OverviewContext = createContext<OverviewContextType | undefined>(undefined);

// URL parameter parsing utilities
const parseEnumParam = <T extends string>(
  value: string | null,
  validValues: readonly T[]
): T | null => {
  return value && validValues.includes(value as T) ? (value as T) : null;
};

const parseOverviewStateFromUrl = (searchParams: URLSearchParams): OverviewState => {
  const parsedState = { ...DEFAULT_OVERVIEW_STATE };

  // Parse start date if provided and use it as the selected date for calculations
  const startDateParam = searchParams.get('startDate');
  let selectedDate = new Date();
  if (startDateParam) {
    const parsedStartDate = new Date(startDateParam);
    if (!isNaN(parsedStartDate.getTime())) {
      selectedDate = parsedStartDate;
    }
  }

  // Parse enum parameters
  const timingTab = parseEnumParam(searchParams.get('timingtab'), ['weekly', 'monthly', 'yearly'] as const);
  const assetTab = parseEnumParam(searchParams.get('assettab'), ['finance', 'liquidity'] as const);
  const analysis = parseEnumParam(searchParams.get('analysis'), ['distribution', 'trends'] as const);
  const card = parseEnumParam(searchParams.get('card'), ['total-expense', 'total-income', 'net-flow', 'money-out', 'money-in', 'net-cash-flow'] as const);
  const groupBy = parseEnumParam(searchParams.get('group-by'), ['categoryGroup', 'category'] as const);

  if (timingTab) parsedState.timingTab = timingTab;
  if (assetTab) parsedState.assetTab = assetTab;
  if (analysis) parsedState.analysisType = analysis;
  if (card) parsedState.expenseCardTab = card;

  // Parse group-by parameter for allocation tabs
  if (groupBy && (parsedState.expenseCardTab === 'total-income' || parsedState.expenseCardTab === 'total-expense')) {
    parsedState.groupBy = groupBy;
  }

  // Parse trending-accumulate parameter for trends analysis
  const trendingAccumulate = searchParams.get('trending-accumulate');
  if (trendingAccumulate !== null) {
    parsedState.trendingAccumulate = trendingAccumulate === 'true';
  }

  // Recalculate date ranges based on the selected date and timing tab
  const dateRange = calculateDateRange(selectedDate, parsedState.timingTab);
  parsedState.startDate = dateRange.startDate;
  parsedState.endDate = dateRange.endDate;

  return parsedState;
};

const formatOverviewStateForUrl = (state: OverviewState): URLSearchParams => {
  const params = new URLSearchParams();

  // Format dates as YYYY-MM-DD using dayjs to avoid timezone issues
  const startDateStr = dayjs(state.startDate).format('YYYY-MM-DD');
  const endDateStr = dayjs(state.endDate).format('YYYY-MM-DD');
  params.set('startDate', startDateStr);
  params.set('endDate', endDateStr);

  // Include income and expenses parameters
  params.set('timingtab', state.timingTab);
  params.set('assettab', state.assetTab);
  params.set('card', state.expenseCardTab);

  // Only include analysis parameter when relevant (not for Net cards)
  if (!['net-flow', 'net-cash-flow'].includes(state.expenseCardTab)) {
    params.set('analysis', state.analysisType);
  }

  // Only include allocation parameters when relevant to the selected card
  if (state.expenseCardTab === 'total-income' || state.expenseCardTab === 'total-expense') {
    params.set('group-by', state.groupBy);
  }

  // Only include trending-accumulate parameter when analysis type is trends
  if (state.analysisType === 'trends' && !['net-flow', 'net-cash-flow'].includes(state.expenseCardTab)) {
    params.set('trending-accumulate', state.trendingAccumulate.toString());
  }

  return params;
};

// Provider component
interface OverviewProviderProps {
  children: ReactNode;
}

export const OverviewProvider: React.FC<OverviewProviderProps> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<OverviewState>(() => {
    // Initialize state from URL parameters on first load
    return parseOverviewStateFromUrl(searchParams);
  });
  const [isInitialized, setIsInitialized] = useState(false);

  // Update URL when state changes, but not during initial load
  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true);
      return;
    }


    const newParams = formatOverviewStateForUrl(state);
    const currentParams = searchParams.toString();
    const newParamsString = newParams.toString();

    // Only update if params actually changed to prevent loops
    if (currentParams !== newParamsString) {
      setSearchParams(newParams, { replace: true });
    }
  }, [state, setSearchParams, searchParams, isInitialized]);

  // Helper to create simple setters
  const createSetter = <K extends keyof OverviewState>(key: K) => {
    return (value: OverviewState[K]) => {
      setState(prev => ({ ...prev, [key]: value }));
    };
  };

  // Special setters with custom logic
  const setTimingTab = (tab: TimingTab) => {
    setState(prev => {
      const currentDate = new Date();
      const newDateRange = calculateDateRange(currentDate, tab);
      return {
        ...prev,
        timingTab: tab,
        startDate: newDateRange.startDate,
        endDate: newDateRange.endDate
      };
    });
  };

  const setSelectedDate = (date: Date) => {
    setState(prev => {
      const newDateRange = calculateDateRange(date, prev.timingTab);
      return {
        ...prev,
        startDate: newDateRange.startDate,
        endDate: newDateRange.endDate
      };
    });
  };

  // Simple setters
  const setAssetTab = createSetter('assetTab');
  const setAnalysisType = createSetter('analysisType');
  const setExpenseCardTab = createSetter('expenseCardTab');
  const setStartDate = createSetter('startDate');
  const setEndDate = createSetter('endDate');
  const setGroupBy = createSetter('groupBy');
  const setTrendingAccumulate = createSetter('trendingAccumulate');

  const setDateRange = (startDate: Date, endDate: Date) => {
    setState(prev => ({ ...prev, startDate, endDate }));
  };

  // Batch update function
  const updateState = (updates: Partial<OverviewState>) => {
    setState(prev => ({ ...prev, ...updates }));
  };

  const contextValue: OverviewContextType = {
    state,
    setTimingTab,
    setAssetTab,
    setAnalysisType,
    setExpenseCardTab,
    setSelectedDate,
    setStartDate,
    setEndDate,
    setDateRange,
    setGroupBy,
    setTrendingAccumulate,
    updateState,
  };

  return (
    <OverviewContext.Provider value={contextValue}>
      {children}
    </OverviewContext.Provider>
  );
};

// Custom hook to use the overview context
export const useOverviewContext = (): OverviewContextType => {
  const context = useContext(OverviewContext);
  if (context === undefined) {
    throw new Error('useOverviewContext must be used within an OverviewProvider');
  }
  return context;
};

// Individual hooks for specific parts of the state
export const useTimingTab = () => {
  const { state, setTimingTab } = useOverviewContext();
  return [state.timingTab, setTimingTab] as const;
};

export const useAssetTab = () => {
  const { state, setAssetTab } = useOverviewContext();
  return [state.assetTab, setAssetTab] as const;
};

export const useAnalysisType = () => {
  const { state, setAnalysisType } = useOverviewContext();
  return [state.analysisType, setAnalysisType] as const;
};

export const useExpenseCardTab = () => {
  const { state, setExpenseCardTab } = useOverviewContext();
  return [state.expenseCardTab, setExpenseCardTab] as const;
};

export const useSelectedDate = () => {
  const { state, setSelectedDate } = useOverviewContext();
  return [state.startDate, setSelectedDate] as const;
};

export const useStartDate = () => {
  const { state, setStartDate } = useOverviewContext();
  return [state.startDate, setStartDate] as const;
};

export const useEndDate = () => {
  const { state, setEndDate } = useOverviewContext();
  return [state.endDate, setEndDate] as const;
};

export const useDateRange = () => {
  const { state, setDateRange } = useOverviewContext();
  return [state.startDate, state.endDate, setDateRange] as const;
};

export const useGroupBy = () => {
  const { state } = useOverviewContext();
  return [state.groupBy] as const;
};

export const useGroupByWithSetter = () => {
  const { state, setGroupBy } = useOverviewContext();
  return [state.groupBy, setGroupBy] as const;
};

export const useTrendingAccumulate = () => {
  const { state } = useOverviewContext();
  return [state.trendingAccumulate] as const;
};

export const useTrendingAccumulateWithSetter = () => {
  const { state, setTrendingAccumulate } = useOverviewContext();
  return [state.trendingAccumulate, setTrendingAccumulate] as const;
}; 